import AttachFile from '@mui/icons-material/AttachFile'
import ExpandMore from '@mui/icons-material/ExpandMore'
import Forward from '@mui/icons-material/Forward'
import Reply from '@mui/icons-material/Reply'
import ReplyAll from '@mui/icons-material/ReplyAll'
import Star from '@mui/icons-material/Star'
import StarBorder from '@mui/icons-material/StarBorder'
import { Alert, Box, Button, Chip, Collapse, IconButton, Skeleton, Tooltip, Typography } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useActions, useLabels, useMessage, useReplyContext, useSettings, useThread } from '../api/hooks'
import type { MessageDetail, MessageSummary } from '../api/types'
import { formatBytes } from '../lib/cache'
import { SenderAvatar } from '../lib/Avatar'
import { sanitizeQuoted, textToEditorHtml } from '../lib/sanitize'
import { useUi } from '../store/ui'
import { InviteCard } from './InviteCard'

const who = (a: { name: string | null; address: string }) => (a.name ? `${a.name} <${a.address}>` : a.address)

function useRemoteImages(contentUrl: string | undefined) {
  return useQuery({ queryKey: ['remote-images', contentUrl], enabled: Boolean(contentUrl), staleTime: Infinity, queryFn: async () => (await fetch(contentUrl!, { method: 'HEAD' })).headers.get('x-has-remote-images') === '1' })
}

function Body({ msg, fill }: { msg: MessageDetail; fill: boolean }) {
  const settings = useSettings()
  const [images, setImages] = useState(false)
  const remote = useRemoteImages(msg.contentUrl)
  const showImages = images || Boolean(settings.data?.loadImages)
  return (
    <>
      {remote.data && !showImages && <Alert severity="info" sx={{ mb: 1 }} action={<Button size="small" onClick={() => setImages(true)}>Display images below</Button>}>Images in this message are hidden to protect your privacy.</Alert>}
      {/* Sandboxed (no allow-scripts), cookie-less separate origin, strict CSP. */}
      <Box component="iframe" title={`Message: ${msg.subject}`} sandbox="" referrerPolicy="no-referrer" src={`${msg.contentUrl}${showImages ? '?images=1' : ''}`}
        sx={{ border: 0, width: '100%', flex: fill ? 1 : 'none', height: fill ? undefined : 420, minHeight: 240, bgcolor: '#fff', borderRadius: 2 }} />
    </>
  )
}

function MessageCard({ id, summary, expanded, onToggle, fill }: { id: string; summary?: MessageSummary; expanded: boolean; onToggle: () => void; fill: boolean }) {
  const q = useMessage(expanded ? id : undefined)
  const labels = useLabels()
  const actions = useActions()
  const openCompose = useUi((s) => s.openCompose)
  const notify = useUi((s) => s.notify)
  const settings = useSettings()
  const reply = useReplyContext()
  const [details, setDetails] = useState(false)
  const m = q.data

  const startReply = (mode: 'reply' | 'replyAll' | 'forward') =>
    reply.mutate({ id, mode }, {
      onSuccess: (ctx) => {
        const sig = settings.data?.signature ? `<p></p><p>-- <br>${textToEditorHtml(settings.data.signature).replace(/^<p>|<\/p>$/g, '')}</p>` : '<p></p>'
        openCompose({ to: ctx.to, cc: ctx.cc, subject: ctx.subject, inReplyTo: ctx.inReplyTo, references: ctx.references, html: sig + sanitizeQuoted(ctx.quotedHtml) })
      },
      onError: () => notify({ message: 'Could not start reply', severity: 'error' }),
    })

  if (!expanded) {
    return (
      <Box role="button" tabIndex={0} onClick={onToggle} onKeyDown={(e) => { if (e.key === 'Enter') onToggle() }} sx={{ px: 3, py: 1.25, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 2, '&:hover': { bgcolor: 'action.hover' } }}>
        <SenderAvatar name={summary?.from.name} address={summary?.from.address} size={28} />
        <Typography noWrap sx={{ width: 180, fontWeight: summary?.seen ? 400 : 700 }}>{summary?.from.name ?? summary?.from.address}</Typography>
        <Typography noWrap color="text.secondary" sx={{ flex: 1 }}>{summary?.snippet ?? summary?.subject}</Typography>
      </Box>
    )
  }
  if (q.isPending) return <Box sx={{ p: 3 }}><Skeleton width="60%" height={32} /><Skeleton width="40%" /><Skeleton variant="rounded" height={200} sx={{ mt: 2 }} /></Box>
  if (!m) return <Alert severity="error" sx={{ m: 2 }}>Couldn't load this message. It may have been moved or deleted.</Alert>

  const sender = m.from
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: fill ? 1 : 'none', minHeight: 0,  }}>
      <Box sx={{ px: 3, pt: fill ? 1 : 2, pb: 1 }}>
        {fill && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
            <Typography variant="h5" component="h2" sx={{ fontSize: 22, wordBreak: 'break-word', fontWeight: 400 }}>{m.subject || '(no subject)'}</Typography>
            {m.labelIds.map((lid) => { const l = labels.data?.find((x) => x.id === lid); return l ? <Chip key={lid} label={l.name} sx={{ bgcolor: l.color, color: '#fff' }} /> : null })}
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <SenderAvatar name={sender?.name} address={sender?.address} size={40} />
          <Box sx={{ flex: 1, minWidth: 0 }} onClick={!fill ? onToggle : undefined}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
              <Typography sx={{ fontWeight: 700 }}>{sender ? (sender.name ?? sender.address) : 'Unknown sender'}</Typography>
              {sender && <Typography variant="caption" color="text.secondary">&lt;{sender.address}&gt;</Typography>}
            </Box>
            <Button size="small" color="inherit" onClick={(e) => { e.stopPropagation(); setDetails((v) => !v) }} sx={{ p: 0, minWidth: 0, color: 'text.secondary', fontWeight: 400 }}>to {m.to.length ? m.to.map((a) => a.name ?? a.address).slice(0, 2).join(', ') + (m.to.length > 2 ? ` +${m.to.length - 2}` : '') : 'undisclosed'}<ExpandMore fontSize="small" /></Button>
            <Collapse in={details}><Box sx={{ mt: 0.5, fontSize: 12, color: 'text.secondary' }}><div>from: {sender ? who(sender) : ''}</div><div>to: {m.to.map(who).join(', ')}</div>{m.cc.length > 0 && <div>cc: {m.cc.map(who).join(', ')}</div>}<div>date: {m.date ? new Date(m.date).toLocaleString() : ''}</div></Box></Collapse>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', pt: 0.5 }}>{m.date ? new Date(m.date).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : ''}</Typography>
          <Tooltip title={m.starred ? 'Starred' : 'Not starred'}><IconButton aria-label={m.starred ? 'Unstar' : 'Star'} onClick={() => actions.run([id], m.starred ? 'unstar' : 'star')} sx={{ color: m.starred ? '#f4b400' : 'text.secondary' }}>{m.starred ? <Star /> : <StarBorder />}</IconButton></Tooltip>
          <Tooltip title="Reply"><IconButton aria-label="Reply" onClick={() => startReply('reply')}><Reply /></IconButton></Tooltip>
        </Box>
      </Box>
      {m.invite && <InviteCard id={id} invite={m.invite} accepted={m.inviteAccepted} />}
      {m.attachments.length > 0 && (
        <Box sx={{ px: 3, pb: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {m.attachments.map((a) => <Chip key={a.partId} icon={<AttachFile />} component="a" clickable href={`/api/messages/${id}/attachments/${a.partId}`} label={`${a.filename} (${formatBytes(a.size)})`} />)}
        </Box>
      )}
      <Box sx={{ px: 3, py: 1, display: 'flex', flexDirection: 'column', flex: fill ? 1 : 'none', minHeight: 0 }}>
        {m.hasHtml || m.text ? <Body msg={m} fill={fill} /> : <Typography color="text.secondary">(empty message)</Typography>}
      </Box>
      <Box sx={{ px: 3, pb: 2, pt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button variant="text" color="inherit" startIcon={<Reply />} onClick={() => startReply('reply')} sx={{ borderRadius: 5, bgcolor: 'action.hover' }}>Reply</Button>
        <Button variant="text" color="inherit" startIcon={<ReplyAll />} onClick={() => startReply('replyAll')} sx={{ borderRadius: 5, bgcolor: 'action.hover' }}>Reply all</Button>
        <Button variant="text" color="inherit" startIcon={<Forward />} onClick={() => startReply('forward')} sx={{ borderRadius: 5, bgcolor: 'action.hover' }}>Forward</Button>
      </Box>
    </Box>
  )
}

/** Conversation view: all messages of a thread stacked, newest expanded; a single message fills the pane. */
export function ConversationView({ id, summary }: { id: string; summary?: MessageSummary }) {
  const isThread = Boolean(summary?.threadId && summary.threadCount > 1)
  const thread = useThread(summary?.threadId, isThread)
  const labels = useLabels()
  const [open, setOpen] = useState<Set<string>>(new Set())
  const items = isThread ? thread.data ?? [] : []

  if (!isThread) return <MessageCard id={id} summary={summary} expanded onToggle={() => undefined} fill />
  if (thread.isPending) return <Box sx={{ p: 3 }}><Skeleton width="50%" height={32} /><Skeleton variant="rounded" height={240} sx={{ mt: 2 }} /></Box>
  const lastId = items.at(-1)?.id
  return (
    <Box sx={{ overflowY: 'auto', height: '100%' }}>
      <Box sx={{ px: 3, pt: 1.5, pb: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="h5" component="h2" sx={{ fontSize: 22, fontWeight: 400 }}>{summary?.subject}</Typography>
        {summary?.labelIds.map((lid) => { const l = labels.data?.find((x) => x.id === lid); return l ? <Chip key={lid} label={l.name} sx={{ bgcolor: l.color, color: '#fff' }} /> : null })}
      </Box>
      {items.map((t) => (
        <MessageCard key={t.id} id={t.id} summary={t} expanded={t.id === lastId || t.id === id || open.has(t.id)} fill={false}
          onToggle={() => setOpen((s) => { const n = new Set(s); if (n.has(t.id)) n.delete(t.id); else n.add(t.id); return n })} />
      ))}
    </Box>
  )
}
